import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n/i18n";
import StickyTitle from "../components/StickyTitle/StickyTitle";
import TransactionForm from "../components/AddForms/TransactionForm";
import RecurringForm from "../components/AddForms/RecurringForm";
import { db, auth } from "../firebase";
import {
	collection,
	addDoc,
	Timestamp,
	updateDoc,
	doc,
} from "firebase/firestore";
import { FilePlus2, CalendarSync } from "lucide-react";
import { toast } from "react-toastify";

const Add = () => {
	const { translate } = useTranslation();
	const [transactionData, setTransactionData] = useState({});
	const [recurringData, setRecurringData] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();

	// Calculate next recurring date
	const calculateNextRecurringDate = (baseDate, frequency, interval) => {
		const nextDate = new Date(baseDate);

		switch (frequency) {
			case "days":
				nextDate.setDate(nextDate.getDate() + interval);
				break;
			case "weeks":
				nextDate.setDate(nextDate.getDate() + 7 * interval);
				break;
			case "months":
				nextDate.setMonth(nextDate.getMonth() + interval);
				break;
			case "years":
				nextDate.setFullYear(nextDate.getFullYear() + interval);
				break;
		}

		return nextDate;
	};

	const handleSubmit = async e => {
		e.preventDefault();

		if (
			!transactionData.title ||
			!transactionData.amount ||
			!transactionData.categoryId
		) {
			toast.error(translate("alert.missingMandatoryFields"));
			return;
		}

		setIsSubmitting(true);

		// Display loading toast
		const loadingToastId = toast.loading(translate("alert.addingTransaction"));

		const userId = auth.currentUser?.uid;
		const selectedDate = new Date(transactionData.date);
		const now = new Date();

		const newTransaction = {
			userId,
			title: transactionData.title,
			amount: parseFloat(transactionData.amount),
			date: Timestamp.fromDate(selectedDate),
			category: transactionData.categoryId,
			subCategory: transactionData.subCategoryId || null,
			type: transactionData.type,
			createdAt: Timestamp.fromDate(now),
			isFromRecurring: recurringData.isRecurring,
		};

		try {
			// Add transaction
			const transactionRef = await addDoc(
				collection(db, "transactions"),
				newTransaction
			);

			// If recurring, create recurring entry and update transaction
			if (recurringData.isRecurring) {
				// For new recurring transactions: calculate nextDate from the selected date
				const nextDate = calculateNextRecurringDate(
					selectedDate,
					recurringData.recurringFrequency,
					recurringData.recurringInterval
				);

				const recurringTransaction = {
					userId,
					title: transactionData.title,
					amount: parseFloat(transactionData.amount),
					category: transactionData.categoryId,
					subCategory: transactionData.subCategoryId || null,
					type: transactionData.type,
					frequency: recurringData.recurringFrequency,
					interval: recurringData.recurringInterval,
					nextDate: Timestamp.fromDate(nextDate), // This is the calculated next occurrence
					referenceDate: Timestamp.fromDate(selectedDate), // Keep original date as reference
					isActive: true,
					createdAt: Timestamp.fromDate(now),
					lastTransactionId: transactionRef.id,
				};

				const recurringRef = await addDoc(
					collection(db, "recurringTransactions"),
					recurringTransaction
				);

				await updateDoc(doc(db, "transactions", transactionRef.id), {
					recurringId: recurringRef.id,
				});
			}

			// Dismiss loading toast and display success
			toast.update(loadingToastId, {
				render: translate("alert.transactionAdded"),
				type: "success",
				isLoading: false,
				autoClose: 3000,
			});
			navigate("/transactions-history");
		} catch (error) {
			console.error(translate("alert.transactionError"), error);
			// Dismiss loading toast and display error
			toast.update(loadingToastId, {
				render: translate("alert.saveError"),
				type: "error",
				isLoading: false,
				autoClose: 3000,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleManageRecurring = () => {
		navigate("/recurring-transactions");
	};

	return (
		<div className="content">
			<StickyTitle title={translate("add.title")} />
			<div className="page-content">
				<form className="design-form add-page-form" onSubmit={handleSubmit}>
					<TransactionForm
						onFormChange={setTransactionData}
						disabled={isSubmitting}
					/>

					<RecurringForm
						onRecurringChange={setRecurringData}
						disabled={isSubmitting}
						transactionDate={transactionData.date}
					/>

					<div className="form-footer ios-buttons">
						<button
							className="ios-button action"
							type="submit"
							disabled={isSubmitting}
						>
							<FilePlus2 size={18} />
							{isSubmitting ? translate("form.adding") : translate("form.add")}
						</button>
						<button
							type="button"
							className="ios-button"
							onClick={handleManageRecurring}
							disabled={isSubmitting}
						>
							<CalendarSync size={18} />
							{translate("form.recurring.manage")}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default Add;
